import { useCallback, useMemo, useRef, useState } from 'react';

import { ja } from '@/i18n/ja';
import { MAX_INPUT_LENGTH } from '@/lib/case-number/constants';
import { runRuleOnlyExtraction } from '@/lib/case-number/pipeline';
import type { EraChoice, NormalizedCaseNumber, UnresolvedCandidate } from '@/lib/case-number/types';
import { copyText } from '@/lib/clipboard';
import { PendingConfirmation } from './PendingConfirmation';
import { ResultCard } from './ResultCard';
import { Button } from './ui/Button';

interface ToolState {
  inputText: string;
  items: NormalizedCaseNumber[];
  unresolvedCandidates: UnresolvedCandidate[];
  error: string | null;
  copiedItemId: string | null;
  copiedAll: boolean;
  hasRun: boolean;
  /** 元号選択で追加した件数。id の重複を避けるためのカウンタ。 */
  pickedCount: number;
}

const INITIAL: ToolState = {
  inputText: '',
  items: [],
  unresolvedCandidates: [],
  error: null,
  copiedItemId: null,
  copiedAll: false,
  hasRun: false,
  pickedCount: 0,
};

const SAMPLE_TEXT =
  '本件では、R5（受）2207、令4行ヒ283及びH30（ワ）1521が参照されています。\n' +
  'なお、平成26年（オ）第77号及び同年（受）第93号も参照。';

export function CaseNumberTool() {
  const [state, setState] = useState<ToolState>(INITIAL);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const charCount = useMemo(() => [...state.inputText].length, [state.inputText]);
  const tooLong = charCount > MAX_INPUT_LENGTH;
  const canSubmit = state.inputText.trim().length > 0 && !tooLong;

  const flashCopied = useCallback((itemId: string | null, all: boolean) => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setState((s) => ({ ...s, copiedItemId: itemId, copiedAll: all }));
    copyTimer.current = setTimeout(() => {
      setState((s) => ({ ...s, copiedItemId: null, copiedAll: false }));
    }, 2000);
  }, []);

  /**
   * 変換。**すべてブラウザ内で完結する。** ここにネットワーク処理は存在しない。
   *
   * 画面の「入力内容はブラウザ内で処理され、サーバーに送信されません」は、
   * この関数に送信コードが 1 行も無いことで担保している。
   * 送信を伴う機能を足す場合は、先に `ja.security.points` の文言を直すこと（README §3）。
   */
  const handleSubmit = useCallback(() => {
    const text = state.inputText;
    if (text.trim().length === 0) return;
    if ([...text].length > MAX_INPUT_LENGTH) {
      setState((s) => ({ ...s, error: ja.errors.tooLong }));
      return;
    }

    const local = runRuleOnlyExtraction(text);
    // 入力は消さない。前回のエラーだけを消す。
    setState((s) => ({
      ...s,
      error: null,
      copiedItemId: null,
      copiedAll: false,
      items: local.items,
      unresolvedCandidates: local.unresolved_candidates,
      hasRun: true,
    }));
  }, [state.inputText]);

  const handleClear = useCallback(() => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setState(INITIAL);
    textareaRef.current?.focus();
  }, []);

  /** 注意書きのリンクから「入力内容の取扱いについて」を開く。JS が無ければ単なるページ内リンクとして機能する。 */
  const openHandlingDisclosure = useCallback(() => {
    const el = document.getElementById('input-handling');
    if (el instanceof HTMLDetailsElement) el.open = true;
  }, []);

  const handleLoadSample = useCallback(() => {
    setState((s) => ({ ...s, inputText: SAMPLE_TEXT, error: null }));
    textareaRef.current?.focus();
  }, []);

  const handleCopyItem = useCallback(
    async (item: NormalizedCaseNumber) => {
      const ok = await copyText(item.normalized);
      if (ok) flashCopied(item.id, false);
    },
    [flashCopied],
  );

  const handleCopyAll = useCallback(async () => {
    // 1行1件。bullet・見出し・空行・未確定の候補は含めない。
    const payload = state.items.map((i) => i.normalized).join('\n');
    const ok = await copyText(payload);
    if (ok) flashCopied(null, true);
  }, [state.items, flashCopied]);

  /**
   * 改元年の元号をユーザーが選んだとき。
   * 再変換させず、その場で変換結果へ移す（件数・すべてコピーも自動で追従する）。
   */
  const handleSelectEra = useCallback((candidate: UnresolvedCandidate, choice: EraChoice) => {
    const pending = candidate.pending_era_selection;
    if (!pending) return;

    setState((s) => {
      const nextIndex = s.pickedCount + 1;
      const picked: NormalizedCaseNumber = {
        id: `case-picked-${nextIndex}`,
        original_fragment: candidate.original_fragment,
        normalized: choice.normalized,
        era: choice.era,
        year: choice.year,
        symbol: pending.symbol,
        serial_number: pending.serial_number,
        context_inferred: false,
        occurrence_count: pending.occurrence_count,
        warnings: [],
      };

      // 元の文章での出現順を保つため、入力文字列上の位置で挿入先を決める
      const positionOf = (fragment: string): number => {
        const at = s.inputText.indexOf(fragment);
        return at >= 0 ? at : Number.MAX_SAFE_INTEGER;
      };
      const target = positionOf(candidate.original_fragment);
      const insertAt = s.items.findIndex((item) => positionOf(item.original_fragment) > target);

      const items = [...s.items];
      if (insertAt === -1) items.push(picked);
      else items.splice(insertAt, 0, picked);

      return {
        ...s,
        items,
        unresolvedCandidates: s.unresolvedCandidates.filter((u) => u !== candidate),
        pickedCount: nextIndex,
      };
    });
  }, []);

  // 「見つかりませんでした」は、確認が必要な候補すら無いときだけ出す
  const showEmpty =
    state.hasRun && state.items.length === 0 && state.unresolvedCandidates.length === 0;

  const showResults = state.items.length > 0;
  const showPending = state.unresolvedCandidates.length > 0;

  return (
    <section className="flex flex-col gap-6">
      {/* ---------- 入力 ---------- */}
      <div>
        <div className="flex flex-wrap items-end justify-between gap-2">
          <label htmlFor="case-number-input" className="block text-[15px] font-bold text-ink">
            {ja.input.label}
          </label>
          <button
            type="button"
            onClick={handleLoadSample}
            aria-label={ja.input.sampleAriaLabel}
            className="rounded border border-line px-2.5 py-1 text-[13px] text-ink-muted hover:bg-surface-muted"
          >
            {ja.input.sample}
          </button>
        </div>
        <p id="case-number-helper" className="mt-1 text-[13px] text-ink-muted">
          {ja.input.helper}
        </p>

        <textarea
          id="case-number-input"
          ref={textareaRef}
          aria-describedby="case-number-helper case-number-privacy case-number-counter"
          aria-invalid={tooLong}
          value={state.inputText}
          onChange={(e) => setState((s) => ({ ...s, inputText: e.target.value }))}
          placeholder={ja.input.placeholder}
          rows={8}
          className="mt-2 w-full resize-y rounded-ln border border-line bg-white p-3.5 text-[15px] leading-relaxed text-ink placeholder:text-ink-subtle/70"
        />

        <div className="mt-2 flex flex-wrap items-start justify-between gap-2">
          {/* 目的の特定と「入れないでほしいもの」だけをここに。詳細は下の折りたたみへ。 */}
          <div className="max-w-[36rem]">
            <p id="case-number-privacy" className="text-[13px] leading-relaxed text-ink-muted">
              {ja.input.handlingNotice}
            </p>
            <p className="mt-1 text-[13px]">
              <a
                href="#input-handling"
                onClick={openHandlingDisclosure}
                className="text-brand underline underline-offset-2 hover:no-underline"
              >
                {ja.input.handlingLink}
              </a>
            </p>
          </div>
          <p
            id="case-number-counter"
            className={`shrink-0 text-[13px] tabular-nums ${tooLong ? 'font-bold text-danger' : 'text-ink-subtle'}`}
          >
            {ja.input.counter(charCount, MAX_INPUT_LENGTH)}
          </p>
        </div>
      </div>

      {/* ---------- 実行 ---------- */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {ja.actions.submit}
        </Button>
        <Button
          variant="secondary"
          onClick={handleClear}
          disabled={state.inputText.length === 0 && state.items.length === 0 && !state.hasRun}
        >
          {ja.actions.clear}
        </Button>
      </div>

      {state.error && (
        <p
          role="alert"
          className="rounded-ln border border-[var(--ln-danger-border)] bg-[var(--ln-danger-bg)] px-4 py-3 text-[15px] text-danger"
        >
          <span aria-hidden="true" className="mr-1.5 font-bold">
            ！
          </span>
          {state.error}
        </p>
      )}

      {/* コピー完了などの状態は色だけに頼らず、読み上げにも伝える */}
      <p aria-live="polite" className="sr-only">
        {state.copiedAll
          ? `${state.items.length}件を${ja.actions.copied}`
          : state.copiedItemId
            ? ja.actions.copied
            : ''}
      </p>

      {/* ---------- 結果 ---------- */}
      {showResults && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-[17px] font-bold text-ink">{ja.results.heading}</h2>
              <p aria-live="polite" className="text-[13px] text-ink-muted">
                {ja.results.count(state.items.length)}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={handleCopyAll}
              aria-label={ja.results.copyAllAriaLabel}
            >
              {state.copiedAll ? (
                <span className="text-success">✓ {ja.actions.copied}</span>
              ) : (
                ja.actions.copyAll
              )}
            </Button>
          </div>

          <ul className="mt-3 flex flex-col gap-3">
            {state.items.map((item) => (
              <ResultCard
                key={item.id}
                item={item}
                copied={state.copiedItemId === item.id}
                onCopy={handleCopyItem}
              />
            ))}
          </ul>
        </div>
      )}

      {showEmpty && (
        <div className="rounded-ln border border-line bg-surface-muted px-4 py-5">
          <p className="text-[15px] font-bold text-ink">{ja.results.emptyTitle}</p>
          <p className="mt-1 text-[13px] text-ink-muted">{ja.results.emptyBody}</p>
        </div>
      )}

      {showPending && (
        <PendingConfirmation
          candidates={state.unresolvedCandidates}
          onSelectEra={handleSelectEra}
        />
      )}
    </section>
  );
}
